import type { CertificationReviewAttributes, UserAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import { MINIMUM_ACCEPTED_CERTIFICATION_SCORE } from '@/client/services/certification/constants'

import CertificationRequestQ from './CertificationRequestQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'

const CertificationReviewQ = {
  ...makeQueryModelV2<CertificationReviewAttributes>('certification_review')([
    'id',
    'subject_user_id',
    'reviewer_user_id',
    'certification_request_id',
    'reviewed_at',
    'review_notes',
    'review_notes_old',
    'reviewer_certification_request_id',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  isFlagged: (): SQLFragment<boolean> =>
    SQL`(
      ${CertificationReviewQ.hasLowScore()} OR
      ${CertificationReviewQ.hasUncheckedBox()} OR
      ${CertificationReviewQ.hasFeedback()}
    )`,

  hasLowScore: (): SQLFragment<boolean> =>
    SQL`
      ${CertificationReviewQ.review_notes} IS NOT NULL AND
      (${CertificationReviewQ.review_notes}->>'reportedScore')::numeric < ${MINIMUM_ACCEPTED_CERTIFICATION_SCORE}
    `,

  hasUncheckedBox: (): SQLFragment<boolean> =>
    SQL`jsonb_array_length(${CertificationReviewQ.review_notes}->'uncheckedItems') > 0`,

  hasFeedback: (): SQLFragment<boolean> =>
    SQL`
      ${CertificationReviewQ.review_notes}->>'userFeedback' != '' AND
      ${CertificationReviewQ.review_notes}->>'userFeedback' IS NOT NULL
    `,

  associations: (): AssociationDefinitions => ({
    CertificationRequest: {
      query: CertificationRequestQ,
      foreignKey: CertificationRequestQ.id,
      sourceKey: CertificationReviewQ.certification_request_id,
    },
  }),

  where: (
    {
      id,
      reviewerUserId,
      subjectUserId,
      certificationRequestId,
      excludeReviewed,
      excludeUnreviewed,
      reviewerCertificationRequestId,
      include = [],
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      reviewerUserId?: number | null | SQLFragment<number>
      subjectUserId?: number | null | SQLFragment<number>
      certificationRequestId?: number | null | SQLFragment<number>
      reviewerCertificationRequestId?: number | null | SQLFragment<number>
      excludeReviewed?: boolean
      excludeUnreviewed?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<CertificationReviewAttributes> => SQL`
        FROM ${CertificationReviewQ}
        ${includeAssociationsToSql(CertificationReviewQ, include)}
        WHERE
            ${paranoid ? SQL`${CertificationReviewQ.deleted_at} IS NULL AND` : SQL``}
            ${id != null ? SQL`${CertificationReviewQ.id} = ${id} AND` : SQL``}
            ${
              reviewerUserId != null
                ? SQL`${CertificationReviewQ.reviewer_user_id} = ${reviewerUserId} AND`
                : SQL``
            }
            ${
              subjectUserId != null
                ? SQL`${CertificationReviewQ.subject_user_id} = ${subjectUserId} AND`
                : SQL``
            }
            ${
              certificationRequestId != null
                ? SQL`${CertificationReviewQ.certification_request_id} = ${certificationRequestId} AND`
                : SQL``
            }
            ${
              reviewerCertificationRequestId != null
                ? SQL`${CertificationReviewQ.reviewer_certification_request_id} = ${reviewerCertificationRequestId} AND`
                : SQL``
            }
            ${
              excludeReviewed === true
                ? SQL`${CertificationReviewQ.reviewed_at} IS NULL AND`
                : SQL``
            }
            ${
              excludeUnreviewed === true
                ? SQL`${CertificationReviewQ.reviewed_at} IS NOT NULL AND`
                : SQL``
            }
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,
  getReviewer: (): SQLFragment<UserAttributes> => SQL`
        ${UserQ.where({
          id: CertificationReviewQ.reviewer_user_id,
        })}
    `,
}

export default CertificationReviewQ
